import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { servicesList } from '@/mocks/en/services';

export default function Services() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  const handleMatchmakingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d7mei75bmnmv4i2ujjr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl">
            <p className="font-grotesk text-micro uppercase tracking-widest text-sienna mb-6 font-extrabold">Our Services</p>
            <h1 className="font-serif text-5xl md:text-7xl text-forest leading-[0.95] mb-8">
              Thoughtful care,<br />
              <span className="italic">designed around</span><br />
              your pet
            </h1>
            <p className="font-grotesk text-lg text-forest/60 max-w-xl leading-relaxed">
              Every service is built on the same principle: your pet deserves care that is intentional, 
              consistent, and genuinely connected to their needs.
            </p>
          </div>
        </section>

        {/* Services Detail */}
        <section className="px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
          <div className="space-y-24 md:space-y-32">
            {servicesList.map((service, index) => (
              <div
                key={service.id}
                id={`service-${service.id}`}
                data-animate
                className={`transition-all duration-700 ${isVisible(`service-${service.id}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="aspect-[4/3] overflow-hidden border border-forest/10">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <p className="font-grotesk text-micro uppercase tracking-widest text-sienna mb-4 font-extrabold">{service.subtitle}</p>
                    <h2 className="font-serif text-3xl md:text-4xl text-forest mb-6">{service.title}</h2>
                    <p className="font-grotesk text-base text-forest/60 leading-relaxed mb-8">{service.description}</p>

                    <ul className="space-y-4 mb-8">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-baseline gap-3">
                          <span className="font-grotesk text-forest/40 text-sm shrink-0 select-none">&mdash;</span>
                          <span className="font-grotesk text-sm text-forest/70">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 mb-8">
                      <div>
                        <p className="font-grotesk text-micro uppercase text-forest/50 mb-1" style={{letterSpacing: '0.1em'}}>Starting at</p>
                        <p className="font-serif text-2xl text-forest font-bold">{service.price}</p>
                      </div>
                      <div className="w-px h-12 hidden sm:block" style={{backgroundColor: 'rgba(30,50,30,0.15)'}} />
                      <div>
                        <p className="font-grotesk text-micro uppercase text-forest/50 mb-1" style={{letterSpacing: '0.1em'}}>Duration</p>
                        <p className="font-grotesk text-sm text-forest">{service.duration}</p>
                      </div>
                    </div>

                    <Link
                      to="/contact"
                      className="inline-block font-grotesk text-sm bg-forest text-cream px-8 py-3 hover:bg-forest-light transition-colors whitespace-nowrap"
                    >
                      Book This Service
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Matchmaking — Flagship Specialized Service */}
        <section
          id="matchmaking"
          data-animate
          className={`px-6 md:px-12 lg:px-20 pt-24 pb-0 md:pt-32 transition-all duration-700 ${isVisible('matchmaking') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Divider */}
          <div className="border-t border-forest/10 mb-24 md:mb-32" />

          {/* Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-end mb-20 md:mb-28">
            <div>
              <p className="font-grotesk text-micro uppercase tracking-widest text-sienna mb-6 font-extrabold">Flagship Service</p>
              <h2 className="font-serif text-4xl md:text-6xl text-forest leading-[0.95]">
                Find your<br />
                <span className="italic">perfect match</span>
              </h2>
            </div>
            <div>
              <p className="font-grotesk text-base text-forest/60 leading-relaxed">
                Matchmaking is an art of compatibility. Share your story, and our consultants will hand-pick the perfect companion for your lifestyle — a service unlike any other in our collection.
              </p>
            </div>
          </div>

          {/* Editorial Image */}
          <div className="w-full h-[50vh] md:h-[70vh] overflow-hidden mb-6">
            <img
              src="https://static.readdy.ai/image/1f65bef57b9643672cbad222c3f4355a/89c2d52ccbb990c330171fb2d93b3b30.webp"
              alt="Two dogs playing in a luxury garden setting"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="flex items-center justify-between mb-24 md:mb-36">
            <p className="font-grotesk text-xs uppercase tracking-widest text-forest/40">Our Companions</p>
            <p className="font-grotesk text-xs text-forest/40 italic">Every match is curated by hand</p>
          </div>
        </section>

        {/* Matchmaking Inquiry Form */}
        <section
          id="matchmaking-form"
          data-animate
          className={`px-6 md:px-12 lg:px-20 py-24 md:py-36 bg-[#f7f4ef] transition-all duration-700 ${isVisible('matchmaking-form') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="max-w-3xl mx-auto">
            <div className="mb-16">
              <h2 className="font-serif text-4xl md:text-5xl text-forest leading-tight mb-6">
                The Matchmaking<br />
                <span className="italic">Inquiry</span>
              </h2>
              <p className="font-grotesk text-base text-forest/55 max-w-lg leading-relaxed">
                Complete the form below and one of our consultants will reach out within 48 hours to begin your journey.
              </p>
            </div>

            {formStatus === 'success' ? (
              <div className="py-16 border-t border-forest/10">
                <p className="font-serif text-2xl text-forest mb-3">Your inquiry has been received.</p>
                <p className="font-grotesk text-sm text-forest/55">Our matchmaking specialist will contact you within 48 hours.</p>
              </div>
            ) : (
              <form
                ref={formRef}
                id="matchmaking-consultation-form"
                data-readdy-form
                onSubmit={handleMatchmakingSubmit}
                className="space-y-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="flex flex-col">
                    <label className="font-grotesk text-[13px] uppercase text-sienna font-bold mb-3" style={{letterSpacing: '0.2em'}}>Full Name</label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Your name"
                      className="bg-transparent border-0 border-b border-forest/30 pb-3 font-grotesk text-sm text-forest placeholder-forest/25 focus:outline-none focus:border-forest transition-colors"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="font-grotesk text-[13px] uppercase text-sienna font-bold mb-3" style={{letterSpacing: '0.2em'}}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="bg-transparent border-0 border-b border-forest/30 pb-3 font-grotesk text-sm text-forest placeholder-forest/25 focus:outline-none focus:border-forest transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="font-grotesk text-[13px] uppercase text-sienna font-bold mb-3" style={{letterSpacing: '0.2em'}}>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 (000) 000-0000"
                    className="bg-transparent border-0 border-b border-forest/30 pb-3 font-grotesk text-sm text-forest placeholder-forest/25 focus:outline-none focus:border-forest transition-colors"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-grotesk text-[13px] uppercase text-sienna font-bold mb-3" style={{letterSpacing: '0.2em'}}>Tell Us Your Story</label>
                  <textarea
                    name="story"
                    rows={4}
                    maxLength={500}
                    placeholder="E.g., My dog&apos;s name is Rosa, she is a white Chow Chow, 10kg. I am looking for a compatible partner for her..."
                    className="bg-transparent border-0 border-b border-forest/30 pb-3 font-grotesk text-sm text-forest focus:outline-none focus:border-forest transition-colors resize-none leading-relaxed"
                  />
                  <span className="font-grotesk text-xs text-forest/30 mt-2 text-right">Max 500 characters</span>
                </div>

                {formStatus === 'error' && (
                  <p className="font-grotesk text-sm text-sienna">Something went wrong. Please try again.</p>
                )}

                <div className="pt-4 flex justify-end">
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-2/3 font-grotesk text-xs font-bold uppercase tracking-widest text-white py-5 rounded-none transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    style={{backgroundColor: '#A65D37'}}
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Begin Consultation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-forest mb-6">
              Not sure what you need?
            </h2>
            <p className="font-grotesk text-base text-forest/60 mb-10 max-w-xl mx-auto">
              Every pet is different. Contact us for a free consultation and we will help you find the right care plan.
            </p>
            <Link
              to="/contact"
              className="inline-block font-grotesk text-sm bg-sienna text-cream px-10 py-4 hover:bg-sienna-light transition-colors whitespace-nowrap"
            >
              Schedule a Consultation
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}